import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Search, Filter, Eye, Edit2, Calendar, MapPin, Ship,
  ChevronDown, ChevronRight, X, Briefcase, Clock, Award,
  Globe, User, Phone, Mail, FileText, History, Target, CheckCircle,
  Plus, Trash2, AlertTriangle, RefreshCw, UserPlus, ArrowRightLeft
} from 'lucide-react';
import { crewApi } from '../services/api';
import type { CrewMember, CrewExperience, ReliefPlan, Rank, CrewFormData } from '../types';
import { RANKS, VESSEL_CATEGORIES } from '../types';

// Modal Component
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md'
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) => {
  if (!isOpen) return null;

  const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`relative bg-white rounded-2xl shadow-xl ${sizes[size]} w-full max-h-[90vh] overflow-hidden`}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">{children}</div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

// Crew Form Component for Create/Edit
const CrewForm = ({
  crew,
  onSubmit,
  onCancel,
  isLoading
}: {
  crew?: CrewMember;
  onSubmit: (data: CrewFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}) => {
  const [formData, setFormData] = useState<CrewFormData>({
    cid: crew?.cid || '',
    name: crew?.name || '',
    rank: crew?.rank || '',
    nationality: crew?.nationality || '',
    status: crew?.status as 'active' | 'available' || 'available',
    date_of_birth: crew?.date_of_birth || '',
    contact_email: crew?.contact_email || '',
    contact_phone: crew?.contact_phone || '',
    emergency_contact: crew?.emergency_contact || '',
    notes: crew?.notes || '',
    vessel: crew?.vessel || crew?.current_vessel || '',
    sign_on_date: crew?.sign_on_date || '',
    due_date: crew?.due_date || '',
    last_vessel: crew?.last_vessel || '',
    last_sign_off: crew?.last_sign_off || '',
    available_from: crew?.available_from || '',
    readiness: crew?.readiness || 'Available',
    crew_type: crew?.crew_type || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isEditing = !!crew;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" />
          Basic Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Crew ID (CID) *
            </label>
            <input
              type="text"
              value={formData.cid}
              onChange={(e) => setFormData({ ...formData, cid: e.target.value })}
              required
              disabled={isEditing}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              placeholder="e.g., 710101"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rank *
            </label>
            <select
              value={formData.rank}
              onChange={(e) => setFormData({ ...formData, rank: e.target.value })}
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select Rank</option>
              {RANKS.map((rank) => (
                <option key={rank.code} value={rank.code}>{rank.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nationality
            </label>
            <input
              type="text"
              value={formData.nationality}
              onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Indian"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status *
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'available' })}
              required
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="available">Available</option>
              <option value="active">Active (On Vessel)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Phone className="w-5 h-5 text-blue-600" />
          Contact Information
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="crew@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="+91 XXXXX XXXXX"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Emergency Contact
            </label>
            <input
              type="text"
              value={formData.emergency_contact}
              onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Name and phone number"
            />
          </div>
        </div>
      </div>

      {/* Assignment Details - Active Crew */}
      {formData.status === 'active' && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Ship className="w-5 h-5 text-blue-600" />
            Current Assignment
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Vessel
              </label>
              <input
                type="text"
                value={formData.vessel}
                onChange={(e) => setFormData({ ...formData, vessel: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Vessel name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sign On Date
              </label>
              <input
                type="date"
                value={formData.sign_on_date}
                onChange={(e) => setFormData({ ...formData, sign_on_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expected Sign Off Date
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Availability Details - Available Crew */}
      {formData.status === 'available' && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Availability Details
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Vessel
              </label>
              <input
                type="text"
                value={formData.last_vessel}
                onChange={(e) => setFormData({ ...formData, last_vessel: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Previous vessel name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Sign Off Date
              </label>
              <input
                type="date"
                value={formData.last_sign_off}
                onChange={(e) => setFormData({ ...formData, last_sign_off: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Available From
              </label>
              <input
                type="date"
                value={formData.available_from}
                onChange={(e) => setFormData({ ...formData, available_from: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Readiness Status
              </label>
              <select
                value={formData.readiness}
                onChange={(e) => setFormData({ ...formData, readiness: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Available">Available</option>
                <option value="On Leave">On Leave</option>
                <option value="Medical Hold">Medical Hold</option>
                <option value="Training">Training</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={3}
          className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Additional notes about the crew member..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-6 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              {isEditing ? 'Updating...' : 'Creating...'}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              {isEditing ? 'Update Crew' : 'Create Crew'}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Delete Confirmation Component
const DeleteConfirmation = ({
  crewName,
  crewCid,
  onConfirm,
  onCancel,
  isLoading
}: {
  crewName: string;
  crewCid: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) => (
  <div className="text-center">
    <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <AlertTriangle className="w-8 h-8 text-red-600" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Crew Member</h3>
    <p className="text-gray-600 mb-6">
      Are you sure you want to delete <span className="font-medium">{crewName}</span> (CID: {crewCid})? 
      This action cannot be undone and will remove all associated records including experience and relief plan references.
    </p>
    <div className="flex justify-center gap-3">
      <button
        onClick={onCancel}
        disabled={isLoading}
        className="px-6 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        disabled={isLoading}
        className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
      >
        {isLoading ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Deleting...
          </>
        ) : (
          <>
            <Trash2 className="w-4 h-4" />
            Delete Crew Member
          </>
        )}
      </button>
    </div>
  </div>
);

// Status Transfer Component
const StatusTransferForm = ({
  crew,
  onTransfer,
  onCancel,
  isLoading
}: {
  crew: CrewMember;
  onTransfer: (newStatus: 'active' | 'available', data: Record<string, string>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) => {
  const currentStatus = crew.status || 'active';
  const newStatus = currentStatus === 'active' ? 'available' : 'active';
  
  const [transferData, setTransferData] = useState<Record<string, string>>({
    vessel: '',
    sign_on_date: '',
    due_date: '',
    sign_off_date: '',
    available_from: new Date().toISOString().split('T')[0],
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center gap-4">
        <div className={`px-4 py-2 rounded-lg ${currentStatus === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {currentStatus === 'active' ? 'Active' : 'Available'}
        </div>
        <ArrowRightLeft className="w-6 h-6 text-gray-400" />
        <div className={`px-4 py-2 rounded-lg ${newStatus === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {newStatus === 'active' ? 'Active' : 'Available'}
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-sm text-gray-600 text-center">
          Transferring <span className="font-medium">{crew.name}</span> from{' '}
          <span className="font-medium">{currentStatus}</span> to{' '}
          <span className="font-medium">{newStatus}</span> status.
        </p>
      </div>

      {newStatus === 'active' ? (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Assignment Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vessel</label>
              <input
                type="text"
                value={transferData.vessel}
                onChange={(e) => setTransferData({ ...transferData, vessel: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Vessel name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sign On Date</label>
              <input
                type="date"
                value={transferData.sign_on_date}
                onChange={(e) => setTransferData({ ...transferData, sign_on_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Sign Off</label>
              <input
                type="date"
                value={transferData.due_date}
                onChange={(e) => setTransferData({ ...transferData, due_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Sign Off Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sign Off Date</label>
              <input
                type="date"
                value={transferData.sign_off_date}
                onChange={(e) => setTransferData({ ...transferData, sign_off_date: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Available From</label>
              <input
                type="date"
                value={transferData.available_from}
                onChange={(e) => setTransferData({ ...transferData, available_from: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="px-6 py-2.5 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onTransfer(newStatus, transferData)}
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              Transferring...
            </>
          ) : (
            <>
              <ArrowRightLeft className="w-4 h-4" />
              Transfer Status
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Crew Detail Component
const CrewDetailView = ({
  crew,
  experience,
  reliefHistory,
  onEdit,
  onAssign
}: {
  crew: CrewMember;
  experience: CrewExperience[];
  reliefHistory: ReliefPlan[];
  onEdit: () => void;
  onAssign: () => void;
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'experience' | 'history'>('overview');

  const getRankColor = (rank?: string) => {
    if (rank?.includes('MSTR') || rank?.includes('Master')) return 'bg-purple-100 text-purple-700';
    if (rank?.includes('COFF') || rank?.includes('Chief Officer')) return 'bg-blue-100 text-blue-700';
    if (rank?.includes('CENG') || rank?.includes('Chief Engineer')) return 'bg-emerald-100 text-emerald-700';
    if (rank?.includes('ENG')) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const totalExperienceMonths = experience.reduce((sum, e) => sum + (e.months_in_rank || 0), 0);

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="flex items-start gap-6">
        <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${getRankColor(crew.rank)}`}>
          <span className="text-2xl font-bold">
            {crew.name?.charAt(0) || 'C'}{crew.surname?.charAt(0) || ''}
          </span>
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {crew.name} {crew.surname}
              </h3>
              <div className="flex items-center gap-3 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRankColor(crew.rank)}`}>
                  {crew.rank}
                </span>
                <span className="flex items-center text-sm text-gray-500">
                  <Globe className="w-4 h-4 mr-1" />
                  {crew.nationality}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={onEdit}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
              {crew.status === 'available' && (
                <button
                  onClick={onAssign}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Target className="w-4 h-4" />
                  Assign
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="p-4 bg-blue-50 rounded-xl text-center">
          <div className="text-2xl font-bold text-blue-600">{crew.cid}</div>
          <div className="text-sm text-blue-700">Crew ID</div>
        </div>
        <div className="p-4 bg-emerald-50 rounded-xl text-center">
          <div className="text-2xl font-bold text-emerald-600">
            {Math.floor(totalExperienceMonths / 12)}y {totalExperienceMonths % 12}m
          </div>
          <div className="text-sm text-emerald-700">Experience</div>
        </div>
        <div className="p-4 bg-purple-50 rounded-xl text-center">
          <div className="text-2xl font-bold text-purple-600">{experience.length}</div>
          <div className="text-sm text-purple-700">Contracts</div>
        </div>
        <div className="p-4 bg-amber-50 rounded-xl text-center">
          <div className={`text-2xl font-bold ${crew.status === 'available' ? 'text-emerald-600' : 'text-amber-600'}`}>
            {crew.status || 'Active'}
          </div>
          <div className="text-sm text-amber-700">Status</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-6">
          {['overview', 'experience', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`pb-4 px-1 font-medium text-sm transition-colors relative ${
                activeTab === tab 
                  ? 'text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {activeTab === tab && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Current Assignment</h4>
            <div className="p-4 bg-gray-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <Ship className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Current Vessel</div>
                  <div className="font-medium text-gray-900">{crew.vessel || crew.current_vessel || '-'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Sign-on Date</div>
                  <div className="font-medium text-gray-900">{crew.sign_on_date || '-'}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Due Date</div>
                  <div className="font-medium text-gray-900">{crew.due_date || crew.due_soff_date || '-'}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Personal Information</h4>
            <div className="p-4 bg-gray-50 rounded-xl space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Full Name</div>
                  <div className="font-medium text-gray-900">{crew.name} {crew.surname}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Nationality</div>
                  <div className="font-medium text-gray-900">{crew.nationality}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-500">Vessel Type Experience</div>
                  <div className="font-medium text-gray-900">{crew.crew_type || crew.vessel_type || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'experience' && (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Experience History ({experience.length} contracts)</h4>
          {experience.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Briefcase className="w-12 h-12 mx-auto mb-2" />
              <p>No experience records found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {experience.map((exp, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 bg-gray-50 rounded-xl flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Ship className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{exp.vessel}</div>
                      <div className="text-sm text-gray-500">
                        {exp.rank} • {exp.vessel_category || 'Unknown Category'}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">
                      {exp.months_in_rank || 0} months
                    </div>
                    <div className="text-sm text-gray-500">
                      {exp.sign_on_date} - {exp.sign_off_date || 'Present'}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          <h4 className="font-semibold text-gray-900">Relief History ({reliefHistory.length} records)</h4>
          {reliefHistory.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <History className="w-12 h-12 mx-auto mb-2" />
              <p>No relief history found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reliefHistory.map((plan, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        plan.role === 'incoming' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {plan.role === 'incoming' ? 'Joined' : 'Relieved'}
                      </span>
                      <span className="font-medium text-gray-900">{plan.vessel_name}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      plan.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {plan.status}
                    </span>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    {plan.rank} • {plan.due_date}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Crew Management Page
export default function CrewManagementPage() {
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [crewExperience, setCrewExperience] = useState<CrewExperience[]>([]);
  const [reliefHistory, setReliefHistory] = useState<ReliefPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'available'>('active');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [totalCrew, setTotalCrew] = useState(0);

  useEffect(() => {
    loadCrew();
  }, [rankFilter, statusFilter]);

  const loadCrew = async () => {
    setIsLoading(true);
    try {
      const response = statusFilter === 'active'
        ? await crewApi.getActiveCrew({ rank: rankFilter || undefined })
        : await crewApi.getAvailableCrew({ rank: rankFilter || undefined });
      
      setCrew(response.crew);
      setTotalCrew(response.total);
    } catch (error) {
      console.error('Failed to load crew:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCrewDetails = async (crewMember: CrewMember) => {
    try {
      const details = await crewApi.getCrewById(crewMember.cid);
      setSelectedCrew(details.crew);
      setCrewExperience(details.experience);
      setReliefHistory(details.relief_history);
    } catch (error) {
      console.error('Failed to load crew details:', error);
      setSelectedCrew(crewMember);
      setCrewExperience([]);
      setReliefHistory([]);
    }
  };

  const handleViewCrew = async (crewMember: CrewMember) => {
    await loadCrewDetails(crewMember);
    setIsDetailModalOpen(true);
  };

  const handleCreateCrew = async (data: CrewFormData) => {
    setIsSaving(true);
    try {
      await crewApi.createCrew(data);
      setIsCreateModalOpen(false);
      loadCrew();
    } catch (error) {
      console.error('Failed to create crew:', error);
      alert('Failed to create crew member. Please check the details and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCrew = async (data: CrewFormData) => {
    if (!selectedCrew) return;
    setIsSaving(true);
    try {
      await crewApi.updateCrew(selectedCrew.cid, data);
      setIsEditModalOpen(false);
      loadCrew();
      loadCrewDetails(selectedCrew);
    } catch (error) {
      console.error('Failed to update crew:', error);
      alert('Failed to update crew member. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCrew = async () => {
    if (!selectedCrew) return;
    setIsSaving(true);
    try {
      await crewApi.deleteCrew(selectedCrew.cid);
      setIsDeleteModalOpen(false);
      setIsDetailModalOpen(false);
      setSelectedCrew(null);
      loadCrew();
    } catch (error) {
      console.error('Failed to delete crew:', error);
      alert('Failed to delete crew member. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTransferStatus = async (newStatus: 'active' | 'available', data: Record<string, string>) => {
    if (!selectedCrew) return;
    setIsSaving(true);
    try {
      await crewApi.transferCrewStatus(selectedCrew.cid, newStatus, data);
      setIsTransferModalOpen(false);
      loadCrew();
      // Update detail view if open
      if (isDetailModalOpen) {
        const details = await crewApi.getCrewById(selectedCrew.cid);
        setSelectedCrew(details.crew);
      }
    } catch (error) {
      console.error('Failed to transfer crew status:', error);
      alert('Failed to transfer crew status. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const openEditModal = (crewMember: CrewMember) => {
    setSelectedCrew(crewMember);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (crewMember: CrewMember) => {
    setSelectedCrew(crewMember);
    setIsDeleteModalOpen(true);
  };

  const openTransferModal = (crewMember: CrewMember) => {
    setSelectedCrew(crewMember);
    setIsTransferModalOpen(true);
  };

  const filteredCrew = crew.filter((c) => {
    const nameMatch = `${c.name} ${c.surname}`.toLowerCase().includes(searchQuery.toLowerCase());
    const cidMatch = c.cid.toLowerCase().includes(searchQuery.toLowerCase());
    const vesselMatch = (c.vessel || c.current_vessel || '').toLowerCase().includes(searchQuery.toLowerCase());
    return nameMatch || cidMatch || vesselMatch;
  });

  const getRankColor = (rank?: string) => {
    if (rank?.includes('MSTR')) return 'bg-purple-100 text-purple-700';
    if (rank?.includes('COFF')) return 'bg-blue-100 text-blue-700';
    if (rank?.includes('CENG')) return 'bg-emerald-100 text-emerald-700';
    if (rank?.includes('ENG')) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Users className="w-8 h-8 mr-3 text-blue-600" />
              Crew Management
            </h1>
            <p className="text-gray-600 mt-2">
              View and manage crew member profiles, experience, and assignments
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <UserPlus className="w-5 h-5" />
            Add Crew Member
          </button>
        </div>

        {/* Status Toggle */}
        <div className="bg-white rounded-xl border border-gray-200 p-2 mb-6 inline-flex">
          <button
            onClick={() => setStatusFilter('active')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              statusFilter === 'active'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Active Crew
          </button>
          <button
            onClick={() => setStatusFilter('available')}
            className={`px-6 py-2.5 rounded-lg font-medium transition-all ${
              statusFilter === 'available'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <CheckCircle className="w-4 h-4 inline mr-2" />
            Available Crew
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, ID, or vessel..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <select
              value={rankFilter}
              onChange={(e) => setRankFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Ranks</option>
              {RANKS.map((rank) => (
                <option key={rank.code} value={rank.code}>{rank.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {RANKS.slice(0, 4).map((rank) => {
            const count = crew.filter(c => c.rank === rank.code).length;
            return (
              <motion.div
                key={rank.code}
                whileHover={{ scale: 1.02 }}
                onClick={() => setRankFilter(rankFilter === rank.code ? '' : rank.code)}
                className={`p-4 rounded-xl border cursor-pointer transition-all ${
                  rankFilter === rank.code 
                    ? getRankColor(rank.code)
                    : 'bg-white border-gray-200 hover:border-blue-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm font-medium">{rank.label}</div>
                  </div>
                  <Briefcase className={`w-8 h-8 opacity-50`} />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Crew Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            </div>
          ) : filteredCrew.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No crew found</h3>
              <p className="text-gray-500">
                {searchQuery ? 'Try adjusting your search' : 'No crew members in this category'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Crew Member</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Rank</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Nationality</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Current Vessel</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Due Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredCrew.map((crewMember, index) => (
                    <motion.tr
                      key={crewMember.cid}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getRankColor(crewMember.rank)}`}>
                            <span className="text-sm font-bold">
                              {crewMember.name?.charAt(0)}{crewMember.surname?.charAt(0)}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {crewMember.name} {crewMember.surname}
                            </div>
                            <div className="text-sm text-gray-500">{crewMember.cid}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRankColor(crewMember.rank)}`}>
                          {crewMember.rank}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Globe className="w-4 h-4" />
                          {crewMember.nationality}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Ship className="w-4 h-4" />
                          {crewMember.vessel || crewMember.current_vessel || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {crewMember.due_date || crewMember.due_soff_date || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          crewMember.readiness?.toLowerCase().includes('avail') || statusFilter === 'available'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {crewMember.readiness || (statusFilter === 'available' ? 'Available' : 'Active')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewCrew(crewMember)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(crewMember)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openTransferModal(crewMember)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-amber-600 font-medium rounded-lg hover:bg-amber-50 transition-colors"
                            title="Transfer Status"
                          >
                            <ArrowRightLeft className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(crewMember)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {filteredCrew.length} of {totalCrew} crew members
          </p>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Crew Profile"
        size="xl"
      >
        {selectedCrew && (
          <CrewDetailView
            crew={selectedCrew}
            experience={crewExperience}
            reliefHistory={reliefHistory}
            onEdit={() => {
              setIsDetailModalOpen(false);
              setIsEditModalOpen(true);
            }}
            onAssign={() => {
              setIsDetailModalOpen(false);
              setIsTransferModalOpen(true);
            }}
          />
        )}
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add New Crew Member"
        size="lg"
      >
        <CrewForm
          onSubmit={handleCreateCrew}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isSaving}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Crew Member"
        size="lg"
      >
        {selectedCrew && (
          <CrewForm
            crew={selectedCrew}
            onSubmit={handleEditCrew}
            onCancel={() => setIsEditModalOpen(false)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirm Deletion"
        size="sm"
      >
        {selectedCrew && (
          <DeleteConfirmation
            crewName={selectedCrew.name || 'Unknown'}
            crewCid={selectedCrew.cid}
            onConfirm={handleDeleteCrew}
            onCancel={() => setIsDeleteModalOpen(false)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* Status Transfer Modal */}
      <Modal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        title="Transfer Crew Status"
        size="md"
      >
        {selectedCrew && (
          <StatusTransferForm
            crew={selectedCrew}
            onTransfer={handleTransferStatus}
            onCancel={() => setIsTransferModalOpen(false)}
            isLoading={isSaving}
          />
        )}
      </Modal>
    </div>
  );
}

